import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Crown, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { trackPurchase } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { fetchMe } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/payments/checkout-status/${sessionId}`);
      if (data.payment_status === "paid") {
        setStatus("paid");
        trackPurchase({ transactionId: sessionId, value: 4.99, itemName: "Premium Subscription" });
        await fetchMe();
        return true;
      }
      if (data.status === "expired") {
        setStatus("expired");
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [sessionId, fetchMe]);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    let count = 0;
    const tick = async () => {
      if (cancelled) return;
      const done = await poll();
      count += 1;
      setAttempts(count);
      if (!done && count < 10) {
        setTimeout(tick, 2000);
      } else if (!done) {
        setStatus("failed");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, poll]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-gray-100 p-8 text-center"
           data-testid="billing-success-page">
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Confirming payment…</h1>
            <p className="text-gray-600 text-sm">Hang tight (attempt {attempts}/10)</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-700" />
            </div>
            <div className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-semibold mb-3">
              <Crown className="w-3.5 h-3.5" /> PREMIUM ACTIVATED
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">You're all set!</h1>
            <p className="text-gray-600 text-sm mb-6">
              30 days of premium access added. Take your first vitality test now.
            </p>
            <Button
              onClick={() => navigate("/")}
              data-testid="billing-go-home-btn"
              className="w-full h-12 rounded-full bg-gradient-to-r from-primary to-emerald-600 text-white font-semibold"
            >
              Back to Home <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <XCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {status === "expired" ? "Session expired" : "Payment couldn't be confirmed"}
            </h1>
            <p className="text-gray-600 text-sm mb-6">
              If you were charged, your access will activate automatically once Stripe confirms.
              Otherwise please try again.
            </p>
            <Link to="/">
              <Button className="w-full h-12 rounded-full bg-gradient-to-r from-primary to-emerald-600 text-white font-semibold">
                Back to Home
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
